import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const NAV = [
  { path: '/app/dashboard', label: 'Intelligence', icon: '🔍' },
  { path: '/app/cases', label: 'Case Tracker', icon: '📁' },
  { path: '/app/documents', label: 'Documents', icon: '📄' },
];

// Secondary tools — kept available if lawyers ask, but not the
// headline pitch. Rendered under a "More" subheader.
const MORE_NAV = [
  { path: '/app/review', label: 'Contract Review', icon: '⚖' },
  { path: '/app/compare', label: 'Compare', icon: '🔄' },
  { path: '/app/templates', label: 'Templates', icon: '📋' },
  { path: '/app/community', label: 'AI Lawyers', icon: '👨‍⚖️' },
  { path: '/app/tools', label: 'Legal Tools', icon: '🔧' },
];

export default function Layout({ children }: { children: React.ReactNode }) {
  const { currentUser, userProfile, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(true);

  return (
    <div className="flex h-screen bg-gray-50">
      <aside className={`${open ? 'w-56' : 'w-16'} flex-shrink-0 bg-slate-900 flex flex-col transition-all duration-200`}>
        <div className="flex items-center gap-2 px-4 py-5 border-b border-slate-700">
          <span className="text-2xl">⚖</span>
          {open && <span className="font-bold text-white text-lg">LexQuant</span>}
        </div>
        <nav className="flex-1 py-4 space-y-1 px-2">
          {NAV.map(({ path, label, icon }) => (
            <NavLink key={path} to={path}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                }`}>
              <span className="w-5 text-center">{icon}</span>
              {open && <span>{label}</span>}
            </NavLink>
          ))}

          {/* More section — secondary tools */}
          {open && (
            <p className="mt-4 mb-1 px-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              More
            </p>
          )}
          {!open && <div className="my-3 border-t border-slate-800" />}
          {MORE_NAV.map(({ path, label, icon }) => (
            <NavLink key={path} to={path}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  isActive ? 'bg-blue-600 text-white' : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
                }`}>
              <span className="w-5 text-center">{icon}</span>
              {open && <span>{label}</span>}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-slate-700 p-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-blue-700 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
              {(userProfile?.display_name || currentUser?.email || 'L')[0].toUpperCase()}
            </div>
            {open && (
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-slate-200 truncate">{userProfile?.display_name || 'User'}</p>
                <button onClick={() => logout().then(() => navigate('/'))} className="text-xs text-slate-400 hover:text-red-400 transition-colors">Sign out</button>
              </div>
            )}
          </div>
        </div>
      </aside>
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white border-b border-gray-200 px-6 py-3 flex items-center gap-4">
          <button onClick={() => setOpen(!open)} className="text-gray-400 hover:text-gray-700 transition-colors text-xl">☰</button>
          <span className="text-sm text-gray-400">全景智能尽调引擎 · Defendant Intelligence</span>
        </header>
        <main className="flex-1 overflow-y-auto p-6">{children}</main>
      </div>
    </div>
  );
}
